import { m, AnimatePresence } from 'motion/react';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useState, useEffect, useEffectEvent, useLayoutEffect, useRef } from 'react';

interface MorphingTextProps {
  phrases: string[];
  interval?: number;
  className?: string;
}

/**
 * MorphingText - Text scramble animation cycling through phrases
 *
 * Features:
 * - Smooth text swap animation
 * - Pauses when tab is not visible
 * - Respects prefers-reduced-motion
 * - aria-live region for screen readers
 */
export function MorphingText({ phrases, interval = 3000, className = '' }: MorphingTextProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const isVisibleRef = useRef(true);
  const shouldReduceMotion = useReducedMotion();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentPhrase = phrases[currentIndex] ?? '';

  // Handle visibility change - pause animation when tab is hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const nextPhrase = useEffectEvent(() => {
    setCurrentIndex((prev) => (prev + 1) % phrases.length);
  });

  useEffect(() => {
    if (phrases.length < 2) return;

    intervalRef.current = setInterval(() => {
      if (isVisibleRef.current) {
        nextPhrase();
      }
    }, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [interval, phrases.length]);

  if (phrases.length === 0) {
    return <span className={className} aria-live="polite" aria-atomic="true" />;
  }

  // Reduced motion: simple crossfade
  if (shouldReduceMotion) {
    return (
      <span className={className} aria-live="polite" aria-atomic="true">
        {currentPhrase}
      </span>
    );
  }

  return (
    <MorphingHeight
      currentIndex={currentIndex}
      currentPhrase={currentPhrase}
      className={className}
    />
  );
}

/**
 * Sizes the rotator to the CURRENT phrase (animated), instead of reserving
 * the tallest phrase's box — that reservation left a line-tall void on
 * desktop and ~250px on mobile whenever a short phrase was up (Interface
 * Craft critique, round 2). The in-flow invisible sizer tracks the live
 * phrase; the wrapper animates between measured heights.
 */
function MorphingHeight({
  currentIndex,
  currentPhrase,
  className,
}: {
  currentIndex: number;
  currentPhrase: string;
  className: string;
}) {
  const sizerRef = useRef<HTMLSpanElement>(null);
  const [height, setHeight] = useState<number | 'auto'>('auto');

  useLayoutEffect(() => {
    const el = sizerRef.current;
    if (!el) return;
    setHeight(el.offsetHeight);
    if (typeof ResizeObserver === 'undefined') return; // jsdom
    const ro = new ResizeObserver(() => setHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [currentIndex]);

  return (
    <m.span
      className="block relative pb-3"
      aria-live="polite"
      aria-atomic="true"
      // react-doctor-disable-next-line react-doctor/no-layout-property-animation -- deliberate: the H1 rotator's height must change in flow so the copy below reflows smoothly; a transform or layout-prop animation scales this box only and the content below would jump (Interface Craft round 2). One 0.3 s ease per phrase, every 3 s.
      animate={{ height }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* In-flow invisible sizer of the CURRENT phrase */}
      <span ref={sizerRef} className={`invisible block ${className}`} aria-hidden="true">
        {currentPhrase}
      </span>
      {/* Animated text positioned absolutely */}
      {/* Simultaneous crossfade (no mode="wait"): the incoming phrase fades in
          while the outgoing fades out, so the headline is never blank. The old
          0.4s blur-out → 0.4s blur-in left the H1 illegible ~27% of each cycle
          (Interface Craft critique, 2026-07-03). */}
      <AnimatePresence initial={false}>
        <m.span
          key={currentIndex}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.25,
            ease: [0.22, 1, 0.36, 1],
          }}
          className={`absolute left-0 right-0 top-0 bottom-auto leading-[1.2] ${className}`}
        >
          {currentPhrase}
        </m.span>
      </AnimatePresence>
    </m.span>
  );
}
